export interface ScanTypeCoverageValue {
  uniqueUsers: number;
  totalEvents: number;
  denominator: number;
  previousUniqueUsers?: number | null;
  previousTotalEvents?: number | null;
}

export type ScanTypeCoverageSliceKey = 'withScan' | 'withoutScan';

export interface ScanTypeCoverageSlice {
  key: ScanTypeCoverageSliceKey;
  label: string;
  count: number;
  percent: number;
  color: string;
}

const SCAN_TYPE_SLICE_COLORS: Record<ScanTypeCoverageSliceKey, string> = {
  withScan: '#0d9488',
  withoutScan: '#b7dee8',
};

const SCAN_TYPE_SLICE_LABELS: Record<ScanTypeCoverageSliceKey, string> = {
  withScan: 'With:',
  withoutScan: 'Without:',
};

/** Away-from-zero to one decimal, matching LimeV3 completeness percents. */
function percentOf(count: number, denominator: number): number {
  if (denominator <= 0) {
    return 0;
  }
  const raw = (count * 100) / denominator;
  return Math.sign(raw) * Math.round(Math.abs(raw) * 10) / 10;
}

export interface ScanCompletenessValue {
  denominator: number;
  threeOfThree: number;
  twoOfThree: number;
  oneOfThree: number;
  zeroOfThree: number;
  threeOfThreePercent: number;
  twoOfThreePercent: number;
  oneOfThreePercent: number;
  zeroOfThreePercent: number;
  scanCount: number;
  wearableDays: number;
  previousThreeOfThree?: number | null;
  previousTwoOfThree?: number | null;
  previousOneOfThree?: number | null;
  previousZeroOfThree?: number | null;
  previousThreeOfThreePercent?: number | null;
  previousTwoOfThreePercent?: number | null;
  previousOneOfThreePercent?: number | null;
  previousZeroOfThreePercent?: number | null;
}

export type ScanCompletenessSliceKey =
  | 'threeOfThree'
  | 'twoOfThree'
  | 'oneOfThree'
  | 'zeroOfThree';

export interface ScanCompletenessSlice {
  key: ScanCompletenessSliceKey;
  label: string;
  count: number;
  percent: number;
  color: string;
  previousCount: number | null;
  previousPercent: number | null;
}

/** One fill for the completeness radar, legend, and tooltip. */
export const COMPLETENESS_RADAR_COLOR = '#036939';

/** Overlay for the previous 28-day completeness radar. */
export const COMPLETENESS_PREVIOUS_RADAR_COLOR = '#ee0004';

const SLICE_COLORS: Record<ScanCompletenessSliceKey, string> = {
  threeOfThree: COMPLETENESS_RADAR_COLOR,
  twoOfThree: COMPLETENESS_RADAR_COLOR,
  oneOfThree: COMPLETENESS_RADAR_COLOR,
  zeroOfThree: COMPLETENESS_RADAR_COLOR,
};

const SLICE_LABELS: Record<ScanCompletenessSliceKey, string> = {
  threeOfThree: '3/3',
  twoOfThree: '2/3',
  oneOfThree: '1/3',
  zeroOfThree: '0/3',
};

function readNumber(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function readOptionalNumber(record: Record<string, unknown>, camel: string, pascal: string): number | null {
  if (!Object.prototype.hasOwnProperty.call(record, camel)
    && !Object.prototype.hasOwnProperty.call(record, pascal)) {
    return null;
  }

  const raw = record[camel] ?? record[pascal];
  if (raw == null || raw === '') {
    return null;
  }

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function toScanCompletenessValue(raw: unknown): ScanCompletenessValue | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const v = raw as Record<string, unknown>;
  return {
    denominator: readNumber(v.denominator ?? v.Denominator),
    threeOfThree: readNumber(v.threeOfThree ?? v.ThreeOfThree),
    twoOfThree: readNumber(v.twoOfThree ?? v.TwoOfThree),
    oneOfThree: readNumber(v.oneOfThree ?? v.OneOfThree),
    zeroOfThree: readNumber(v.zeroOfThree ?? v.ZeroOfThree),
    threeOfThreePercent: readNumber(v.threeOfThreePercent ?? v.ThreeOfThreePercent),
    twoOfThreePercent: readNumber(v.twoOfThreePercent ?? v.TwoOfThreePercent),
    oneOfThreePercent: readNumber(v.oneOfThreePercent ?? v.OneOfThreePercent),
    zeroOfThreePercent: readNumber(v.zeroOfThreePercent ?? v.ZeroOfThreePercent),
    scanCount: readNumber(v.scanCount ?? v.ScanCount),
    wearableDays: readNumber(v.wearableDays ?? v.WearableDays),
    previousThreeOfThree: readOptionalNumber(v, 'previousThreeOfThree', 'PreviousThreeOfThree'),
    previousTwoOfThree: readOptionalNumber(v, 'previousTwoOfThree', 'PreviousTwoOfThree'),
    previousOneOfThree: readOptionalNumber(v, 'previousOneOfThree', 'PreviousOneOfThree'),
    previousZeroOfThree: readOptionalNumber(v, 'previousZeroOfThree', 'PreviousZeroOfThree'),
    previousThreeOfThreePercent: readOptionalNumber(
      v,
      'previousThreeOfThreePercent',
      'PreviousThreeOfThreePercent',
    ),
    previousTwoOfThreePercent: readOptionalNumber(
      v,
      'previousTwoOfThreePercent',
      'PreviousTwoOfThreePercent',
    ),
    previousOneOfThreePercent: readOptionalNumber(
      v,
      'previousOneOfThreePercent',
      'PreviousOneOfThreePercent',
    ),
    previousZeroOfThreePercent: readOptionalNumber(
      v,
      'previousZeroOfThreePercent',
      'PreviousZeroOfThreePercent',
    ),
  };
}

export function toScanTypeCoverageValue(raw: unknown): ScanTypeCoverageValue | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const v = raw as Record<string, unknown>;
  return {
    uniqueUsers: readNumber(v.uniqueUsers ?? v.UniqueUsers),
    totalEvents: readNumber(v.totalEvents ?? v.TotalEvents),
    denominator: readNumber(v.denominator ?? v.Denominator),
    previousUniqueUsers: readOptionalNumber(v, 'previousUniqueUsers', 'PreviousUniqueUsers'),
    previousTotalEvents: readOptionalNumber(v, 'previousTotalEvents', 'PreviousTotalEvents'),
  };
}

export function scanTypeHasActivity(value: ScanTypeCoverageValue | null | undefined): boolean {
  if (!value) {
    return false;
  }

  return value.uniqueUsers > 0 || value.totalEvents > 0;
}

export function scanCompletenessHasActivity(value: ScanCompletenessValue | null | undefined): boolean {
  if (!value) {
    return false;
  }

  return value.threeOfThree + value.twoOfThree + value.oneOfThree > 0;
}

export function scanTypeCoverageSlices(
  value: ScanTypeCoverageValue | null | undefined,
): ScanTypeCoverageSlice[] {
  const uniqueUsers = Math.max(0, value?.uniqueUsers ?? 0);
  const denominator = Math.max(0, value?.denominator ?? 0);
  const withCount = Math.min(uniqueUsers, denominator);
  const withoutCount = Math.max(0, denominator - withCount);

  return [
    {
      key: 'withScan',
      label: SCAN_TYPE_SLICE_LABELS.withScan,
      count: withCount,
      percent: percentOf(withCount, denominator),
      color: SCAN_TYPE_SLICE_COLORS.withScan,
    },
    {
      key: 'withoutScan',
      label: SCAN_TYPE_SLICE_LABELS.withoutScan,
      count: withoutCount,
      percent: percentOf(withoutCount, denominator),
      color: SCAN_TYPE_SLICE_COLORS.withoutScan,
    },
  ];
}

export function scanCompletenessSlices(
  value: ScanCompletenessValue | null | undefined,
): ScanCompletenessSlice[] {
  const source: ScanCompletenessValue = value ?? {
    denominator: 0,
    threeOfThree: 0,
    twoOfThree: 0,
    oneOfThree: 0,
    zeroOfThree: 0,
    threeOfThreePercent: 0,
    twoOfThreePercent: 0,
    oneOfThreePercent: 0,
    zeroOfThreePercent: 0,
    scanCount: 0,
    wearableDays: 0,
  };

  const keys: ScanCompletenessSliceKey[] = [
    'threeOfThree',
    'twoOfThree',
    'oneOfThree',
    'zeroOfThree',
  ];

  return keys.map((key) => ({
    key,
    label: SLICE_LABELS[key],
    count: source[key],
    percent: source[`${key}Percent` as keyof ScanCompletenessValue] as number,
    color: SLICE_COLORS[key],
    previousCount: previousBucketCount(source, key),
    previousPercent: previousBucketPercent(source, key),
  }));
}

function previousBucketCount(
  source: ScanCompletenessValue,
  key: ScanCompletenessSliceKey,
): number | null {
  if (key === 'threeOfThree') {
    return source.previousThreeOfThree ?? null;
  }
  if (key === 'twoOfThree') {
    return source.previousTwoOfThree ?? null;
  }
  if (key === 'oneOfThree') {
    return source.previousOneOfThree ?? null;
  }
  return source.previousZeroOfThree ?? null;
}

function previousBucketPercent(
  source: ScanCompletenessValue,
  key: ScanCompletenessSliceKey,
): number | null {
  if (key === 'threeOfThree') {
    return source.previousThreeOfThreePercent ?? null;
  }
  if (key === 'twoOfThree') {
    return source.previousTwoOfThreePercent ?? null;
  }
  if (key === 'oneOfThree') {
    return source.previousOneOfThreePercent ?? null;
  }
  return source.previousZeroOfThreePercent ?? null;
}

export function hasPreviousCompleteness(slices: ScanCompletenessSlice[]): boolean {
  return slices.some((slice) => slice.previousPercent != null);
}

/** Radius scale for the completeness radar: largest bucket reaches the outer ring. */
export function radarPercentDomain(percents: number[]): [number, number] {
  const max = Math.max(0, ...percents);
  if (max <= 0) {
    return [0, 100];
  }

  return [0, max];
}
